// What the client must tick to submit. Stored by id, so an id set once
// stays; the wording is the office's.

import type { IntakeLanguage, IntakeTemplateReserved } from "@duralex/shared";
import { languageName } from "../../../../lib/intakeLanguages";
import { move, newConsent, without, type Draft, rowKey } from "./draft";
import { IdField, LabelField, RowActions } from "./fields";

export function ConsentsEditor({
  draft,
  lang,
  reserved,
  existing,
  stale,
  ai,
  read,
  onRead,
  onChange,
}: {
  draft: Draft;
  lang: IntakeLanguage;
  reserved: IntakeTemplateReserved;
  existing: Set<string>;
  /** The keys whose `lang` translation is out of date. */
  stale: Set<string>;
  /** The keys whose `lang` cell is a model's unsaved proposal. */
  ai: Set<string>;
  /** The machine-written consents the office has ticked as read. */
  read: Set<string>;
  onRead: (key: string, read: boolean) => void;
  onChange: (draft: Draft) => void;
}) {
  const set = (consents: Draft["consents"]) => onChange({ ...draft, consents });
  const ids = draft.consents.map((c) => c.id);
  return (
    <div className="ie-list">
      <p className="ie-hint">
        Every consent must be ticked before a questionnaire can be submitted.
      </p>
      {draft.consents.map((c, i) => {
        const isReserved = reserved.consents.includes(c.id);
        return (
          <div key={rowKey(ids, i)} className="ie-doc">
            <div className="ie-q-head">
              <IdField
                label={`Consent id (${c.id})`}
                value={c.id}
                locked={isReserved || existing.has(`consent:${c.id}`)}
                onChange={(id) => set(draft.consents.map((x, j) => (j === i ? { ...x, id } : x)))}
              />
              <RowActions
                what={`consent ${c.id}`}
                onUp={() => set(move(draft.consents, i, -1))}
                onDown={() => set(move(draft.consents, i, 1))}
                onRemove={() => set(without(draft.consents, i))}
                locked={isReserved ? "Submit stores this consent by id" : undefined}
              />
            </div>
            <LabelField
              label={`Consent ${c.id}`}
              text={c.text}
              lang={lang}
              stale={stale.has(`consent.${c.id}`)}
              ai={ai.has(`consent.${c.id}`)}
              multiline
              onChange={(text) => set(draft.consents.map((x, j) => (j === i ? { ...x, text } : x)))}
            />
            {ai.has(`consent.${c.id}`) && (
              <label className="cc-form-check ie-read">
                <input
                  type="checkbox"
                  checked={read.has(`consent.${c.id}`)}
                  onChange={(e) => onRead(`consent.${c.id}`, e.target.checked)}
                />
                {`I read this ${languageName(lang)} text and it says what the office means`}
              </label>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="cc-link-btn"
        onClick={() =>
          set([...draft.consents, newConsent(new Set(draft.consents.map((c) => c.id)))])
        }
      >
        Add a consent
      </button>
    </div>
  );
}
